import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../component/Header'; 
import Footer from '../component/footer';
import Input from '../component/Input';
import { User, CreditCard, LogOut, CheckCircle2, Receipt, Lock } from 'lucide-react';
import { changeUserPassword } from '../services/userService';
import { getUserDashboard, updateUserProfile, DashboardPayment, DashboardSubscription } from '../services/userService';
import { clearAuthenticated, getAuthToken } from '../utils/authSession';
import { showErrorToast, showSuccessToast } from '../utils/toaster';
import { TailSpin } from "react-loader-spinner";


const AccountProfile = () => {
  const navigate = useNavigate();
  // Set default tab to 'payments' to match the screenshot layout
  const [activeTab, setActiveTab] = useState('payments');
  const [isSaved, setIsSaved] = useState(false);
  const [isSavingProfile, setIsSavingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isLoadingDashboard, setIsLoadingDashboard] = useState(true);
  const [payments, setPayments] = useState<DashboardPayment[]>([]);
  const [subscriptions, setSubscriptions] = useState<DashboardSubscription[]>([]);
  
  // State for the avatar image preview
  const [avatarPreview] = useState<string | null>(null);
  
  // Form State for Account Details (Updated to match the screenshot fields)
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    displayName: '',
    email: '',
    currentPass: '',
    newPass: '',
    confirmPass: ''
  });

  useEffect(() => {
    const token = getAuthToken();

    if (!token) {
      showErrorToast('Your session has expired. Please login again.');
      clearAuthenticated();
      navigate('/login?mode=signup', { replace: true });
      return;
    }

    let isMounted = true;

    const fetchDashboard = async () => {
      try {
        setIsLoadingDashboard(true);
        const response = await getUserDashboard(token);

        if (!isMounted) {
          return;
        }

        const dashboardData = response.data;
        setPayments(dashboardData.payments || []);
        setSubscriptions(dashboardData.subscriptions || []);
        setFormData((prev) => ({
          ...prev,
          firstName: dashboardData.firstname || '',
          lastName: dashboardData.lastname || '',
          displayName: dashboardData.user_meta?.display_name || dashboardData.firstname || '',
          email: dashboardData.email || '',
        }));
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Failed to load dashboard data.';
        showErrorToast(message);
      } finally {
        if (isMounted) {
          setIsLoadingDashboard(false);
        }
      }
    };

    fetchDashboard();

    return () => {
      isMounted = false;
    };
  }, [navigate]);

  const formatDate = (value: string) => {
    if (!value) {
      return '-';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleDateString();
  };

  const formatPrice = (value: number) => `EUR ${value}`;

  const normalizeStatus = (value: string) => (value || '').replace(/_/g, ' ').toUpperCase();

  const handleLogout = () => {
    if (typeof window !== 'undefined') {
      window.localStorage.clear();
    }
    clearAuthenticated();
    navigate('/login', { replace: true });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({ ...formData, [e.target.id]: e.target.value });
    setIsSaved(false);
  };

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const token = getAuthToken();

    if (!token) {
      showErrorToast('Your session has expired. Please login again.');
      clearAuthenticated();
      navigate('/login?mode=signup', { replace: true });
      return;
    }

    try {
      setIsSavingProfile(true);
      const response = await updateUserProfile(token, {
        firstname: formData.firstName.trim(),
        lastname: formData.lastName.trim(),
        display_name: formData.displayName.trim(),
      });

      setIsSaved(true);
      showSuccessToast(response.message || 'Profile updated successfully.');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update profile.';
      showErrorToast(message);
    } finally {
      setIsSavingProfile(false);
    }
  };



  // Tabs updated to match the reference images
  const tabs = [    
    { id: 'payments', label: 'Payments', icon: <Receipt size={20} /> },
    { id: 'subscriptions', label: 'My subscriptions', icon: <CreditCard size={20} /> },
    { id: 'account', label: 'Account Details', icon: <User size={20} /> },
    { id: 'security', label: 'Change Password', icon: <Lock size={20} /> },
  ];

  return (
    <>
      <Header />
      <div className="min-h-screen flex flex-col bg-[#F4F5F7] font-sans text-gray-800">
        <main className="flex flex-col flex-grow w-full gap-8 px-4 py-10 mx-auto max-w-7xl md:px-12 md:py-16 lg:flex-row lg:gap-12 md:pt-32">
          
          {/* =========================================
              PROFILE SIDEBAR NAVIGATION
          ========================================= */}
          <aside className="w-full lg:w-[280px] flex-shrink-0">
            {/* User Brief Overview */}
            <div className="bg-white rounded-[2rem] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 mb-6 flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-[#113A28] text-[#4CE470] flex items-center justify-center font-black text-2xl flex-shrink-0 overflow-hidden">
                {avatarPreview ? (
                  <img src={avatarPreview} alt="Profile" className="object-cover w-full h-full" />
                ) : (
                  <>{formData.firstName.charAt(0)}{formData.lastName.charAt(0)}</>
                )}
              </div>
              <div className="overflow-hidden">
                <h2 className="text-xl font-extrabold text-[#113A28] truncate">{formData.firstName} {formData.lastName}</h2>
                <p className="text-sm font-medium text-gray-500 truncate">{formData.email}</p>
              </div>
            </div>

            {/* Navigation Menu */}
            <div className="bg-white rounded-[2rem] p-3 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 lg:sticky lg:top-[12vh]">
              <nav className="flex flex-row lg:flex-col overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden snap-x">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-3 px-5 py-4 rounded-xl font-bold text-sm transition-all duration-300 whitespace-nowrap snap-start shrink-0 lg:w-full ${
                      activeTab === tab.id
                        ? 'bg-[#113A28] text-white shadow-md'
                        : 'text-gray-500 hover:bg-gray-50 hover:text-[#113A28]'
                    }`}
                  >
                    <span className={activeTab === tab.id ? 'text-[#4CE470]' : 'text-gray-400'}>
                      {tab.icon}
                    </span>
                    {tab.label}
                  </button>
                ))}
                
                <div className="hidden h-px mx-4 my-2 bg-gray-100 lg:block"></div>
                
                <button onClick={handleLogout} className="flex items-center gap-3 px-5 py-4 text-sm font-bold text-red-500 transition-all duration-300 rounded-xl hover:bg-red-50 whitespace-nowrap snap-start shrink-0 lg:w-full">
                  <LogOut size={20} className="text-red-400" />
                  Log Out
                </button>
              </nav>
            </div>
          </aside>

          {/* =========================================
              MAIN CONTENT AREA
          ========================================= */}
          <div className="flex-1 bg-white rounded-[2.5rem] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 p-6 md:p-12 h-fit min-h-[600px]">
            {isLoadingDashboard ? (
              <div className="flex items-center justify-center h-[320px] text-gray-500 font-semibold"><TailSpin color="rgb(17,58,40)" height={80} width={80} /></div>
            ) : (
              <>
            
            {/* --- TAB: PAYMENTS --- */}
            {activeTab === 'payments' && (
              <div className="duration-500 animate-in fade-in slide-in-from-bottom-4">
                <h1 className="text-2xl md:text-3xl font-extrabold text-[#113A28] mb-8 tracking-tight">Payments</h1>
                
                {/* Responsive Table Wrapper */}
                <div className="w-full overflow-x-auto border border-gray-100 rounded-2xl">
                  <table className="w-full text-left border-collapse min-w-[600px]">
                    <thead>
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <th className="px-6 py-4 text-xs font-bold tracking-wider text-gray-500 uppercase">Payments</th>
                        <th className="px-6 py-4 text-xs font-bold tracking-wider text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-4 text-xs font-bold tracking-wider text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-4 text-xs font-bold tracking-wider text-gray-500 uppercase">Total</th>
                        <th className="px-6 py-4 text-xs font-bold tracking-wider text-gray-500 uppercase"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {payments.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="px-6 py-8 text-sm text-center text-gray-500">No payments found.</td>
                        </tr>
                      ) : (
                        payments.map((payment) => (
                          <tr key={payment.payment_id} className="transition-colors hover:bg-gray-50/50">
                            <td className="px-6 py-5 text-sm font-medium text-[#113A28]">{payment.payment_id}</td>
                            <td className="px-6 py-5 text-sm text-gray-500">{formatDate(payment.date)}</td>
                            <td className="px-6 py-5">
                              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold bg-[#4CE470]/10 text-[#113A28] border border-[#4CE470]/20">
                                {normalizeStatus(payment.status)}
                              </span>
                            </td>
                            <td className="px-6 py-5 text-sm font-bold text-[#113A28]">{formatPrice(payment.price)}</td>
                            {/* <td className="px-6 py-5 text-right">
                              <button className="text-xs font-bold bg-white text-[#113A28] border border-gray-200 px-4 py-2 rounded-lg hover:bg-[#113A28] hover:text-white transition-colors">
                                Invoice
                              </button>
                            </td> */}
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* --- TAB: MY SUBSCRIPTIONS --- */}
            {activeTab === 'subscriptions' && (
              <div className="duration-500 animate-in fade-in slide-in-from-bottom-4">
                <h1 className="text-2xl md:text-3xl font-extrabold text-[#113A28] mb-8 tracking-tight">My subscriptions</h1>
                
                <div className="space-y-10">
                  {/* Section 1: Subscription Details */}
                  <div className="overflow-hidden border border-gray-100 shadow-sm rounded-2xl">
                    <div className="divide-y divide-gray-100">
                      {subscriptions.length === 0 ? (
                        <div className="px-6 py-8 text-sm text-center text-gray-500">No subscriptions found.</div>
                      ) : (
                        (() => {
                          const activeSubscription = subscriptions[0];
                          return (
                            <>
                              <div className="flex flex-col justify-between px-6 py-4 sm:flex-row sm:items-center bg-gray-50/50">
                                <span className="text-sm font-bold text-gray-500">Status</span>
                                <span className="text-sm font-bold text-[#113A28]">{normalizeStatus(activeSubscription.status)}</span>
                              </div>
                              <div className="flex flex-col justify-between px-6 py-4 sm:flex-row sm:items-center">
                                <span className="text-sm font-bold text-gray-500">Start Date</span>
                                <span className="text-sm font-medium text-gray-800">{formatDate(activeSubscription.starts_at)}</span>
                              </div>
                              <div className="flex flex-col justify-between px-6 py-4 sm:flex-row sm:items-center bg-gray-50/50">
                                <span className="text-sm font-bold text-gray-500">End Date</span>
                                <span className="text-sm font-medium text-gray-800">{formatDate(activeSubscription.ends_at)}</span>
                              </div>
                              <div className="flex flex-col justify-between px-6 py-4 sm:flex-row sm:items-center">
                                <span className="text-sm font-bold text-gray-500">Payment</span>
                                <span className="text-sm font-medium text-gray-800">{normalizeStatus(activeSubscription.payment_status)}</span>
                              </div>
                              <div className="flex flex-col justify-between px-6 py-4 sm:flex-row sm:items-center bg-gray-50/50">
                                <span className="mb-2 text-sm font-bold text-gray-500 sm:mb-0">Plan</span>
                                <span className="text-sm font-medium text-gray-800">{activeSubscription.plan_name}</span>
                              </div>
                            </>
                          );
                        })()
                      )}
                    </div>
                  </div>

                  {/* Section 2: Subscription Totals */}
                  <div>
                    <h2 className="text-xl font-extrabold text-[#113A28] mb-4">Subscription totals</h2>
                    <div className="overflow-hidden border border-gray-100 shadow-sm rounded-2xl">
                      <div className="flex items-center justify-between px-6 py-3 border-b border-gray-100 bg-gray-50">
                        <span className="text-xs font-bold tracking-wider text-gray-500 uppercase">Product</span>
                        <span className="text-xs font-bold tracking-wider text-gray-500 uppercase">Total</span>
                      </div>
                      <div className="divide-y divide-gray-100">
                        <div className="flex items-center justify-between px-6 py-4">
                          <span className="text-sm font-medium text-gray-800">
                            {subscriptions[0]?.plan_name || 'No active plan'}
                          </span>
                          <span className="text-sm font-bold text-[#113A28]">{formatPrice(subscriptions[0]?.price || 0)}</span>
                        </div>
                        <div className="flex items-center justify-between px-6 py-4 bg-gray-50/50">
                          <span className="text-sm font-bold text-gray-500">SubTotal:</span>
                          <span className="text-sm font-bold text-[#113A28]">{formatPrice(subscriptions[0]?.price || 0)}</span>
                        </div>
                        <div className="flex items-center justify-between px-6 py-4">
                          <span className="text-sm font-extrabold text-[#113A28]">Total:</span>
                          <span className="text-sm font-extrabold text-[#113A28]">{formatPrice(subscriptions[0]?.price || 0)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Section 3: Billing Address */}
                  <div>
                    <h2 className="text-xl font-extrabold text-[#113A28] mb-4">Billing address</h2>
                    <div className="overflow-hidden border border-gray-100 shadow-sm rounded-2xl">
                      <div className="divide-y divide-gray-100">
                        <div className="flex flex-col justify-between px-6 py-4 sm:flex-row sm:items-center">
                          <span className="text-sm font-bold text-gray-500">Address</span>
                          <span className="text-sm font-medium text-gray-800">test</span>
                        </div>
                        <div className="flex flex-col justify-between px-6 py-4 sm:flex-row sm:items-center bg-gray-50/50">
                          <span className="text-sm font-bold text-gray-500">Country</span>
                          <span className="text-sm font-medium text-gray-800">test</span>
                        </div>
                        <div className="flex flex-col justify-between px-6 py-4 sm:flex-row sm:items-center">
                          <span className="text-sm font-bold text-gray-500">City</span>
                          <span className="text-sm font-medium text-gray-800">test</span>
                        </div>
                        <div className="flex flex-col justify-between px-6 py-4 sm:flex-row sm:items-center bg-gray-50/50">
                          <span className="text-sm font-bold text-gray-500">State</span>
                          <span className="text-sm font-medium text-gray-800">test</span>
                        </div>
                        <div className="flex flex-col justify-between px-6 py-4 sm:flex-row sm:items-center">
                          <span className="text-sm font-bold text-gray-500">Email</span>
                          <span className="text-sm font-medium text-gray-800">{formData.email || '-'}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            )}

            {/* --- TAB: ACCOUNT DETAILS --- */}
            {activeTab === 'account' && (
              <div className="duration-500 animate-in fade-in slide-in-from-bottom-4">
                <h1 className="text-2xl md:text-3xl font-extrabold text-[#113A28] mb-8 tracking-tight">Account Details</h1>
                <form onSubmit={handleSave} className="max-w-2xl space-y-6">
                  {/* Names Row */}
                  <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                    <Input 
                      label="First Name" 
                      id="firstName" 
                      value={formData.firstName} 
                      onChange={handleInputChange} 
                      required 
                    />
                    <Input 
                      label="Last Name" 
                      id="lastName" 
                      value={formData.lastName} 
                      onChange={handleInputChange} 
                      required 
                    />
                  </div>

                  <Input 
                    label="Display Name" 
                    id="displayName" 
                    value={formData.displayName} 
                    onChange={handleInputChange} 
                  />

                  <Input 
                    label="Email / Username*" 
                    type="email" 
                    id="email" 
                    value={formData.email} 
                    disabled
                    required 
                  />

                  {/* Password Change moved to separate Security tab */}

                  {/* Save Button */}
                  <div className="flex items-center justify-between pt-6 border-t border-gray-100">
                    {isSaved ? (
                      <div className="flex items-center gap-2 text-[#4CE470] font-bold text-sm bg-[#4CE470]/10 px-4 py-2 rounded-lg">
                        <CheckCircle2 size={18} /> Changes saved successfully!
                      </div>
                    ) : (
                      <div></div>
                    )}
                    <button type="submit" disabled={isSavingProfile} className="bg-[#113A28] text-white px-8 py-4 rounded-xl font-black text-sm tracking-wide hover:bg-[#1a573c] hover:shadow-lg hover:-translate-y-0.5 transition-all w-full md:w-auto disabled:opacity-60 disabled:cursor-not-allowed">
                      {isSavingProfile ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              </div>
            )}
            {activeTab === 'security' && (
              <div className="duration-500 animate-in fade-in slide-in-from-bottom-4">
                <h1 className="text-2xl md:text-3xl font-extrabold text-[#113A28] mb-8 tracking-tight">Change Password</h1>
                <form onSubmit={async (e) => {
                  e.preventDefault();
                  if (!formData.newPass) {
                    showErrorToast('Please enter a new password.');
                    return;
                  }
                  if (formData.newPass !== formData.confirmPass) {
                    showErrorToast('New password and confirmation do not match.');
                    return;
                  }

                  const token = getAuthToken();
                  if (!token) {
                    showErrorToast('Session expired. Please login again.');
                    clearAuthenticated();
                    navigate('/login?mode=signup');
                    return;
                  }

                  try {
                    setIsChangingPassword(true);
                    await changeUserPassword(token, { old_password: formData.currentPass || undefined, new_password: formData.newPass });
                    showSuccessToast('Password updated successfully.');
                    setFormData({ ...formData, currentPass: '', newPass: '', confirmPass: '' });
                  } catch (err: any) {
                    showErrorToast(err?.message || 'Failed to change password.');
                  } finally {
                    setIsChangingPassword(false);
                  }
                }} className="max-w-xl space-y-6">
                  <Input label="Current password" type="password" id="currentPass" value={formData.currentPass} onChange={handleInputChange} />
                  <Input label="New password" type="password" id="newPass" value={formData.newPass} onChange={handleInputChange} />
                  <Input label="Confirm new password" type="password" id="confirmPass" value={formData.confirmPass} onChange={handleInputChange} />
                  <div className="flex items-center justify-end gap-4">
                    <button type="submit" disabled={isChangingPassword} className="bg-[#113A28] text-white px-6 py-3 rounded-md font-bold disabled:opacity-60">{isChangingPassword ? 'Updating...' : 'Update Password'}</button>
                  </div>
                </form>
              </div>
            )}
            
              </>
            )}
          </div>
        </main>
      </div>
      <Footer />
    </>
  );
};

export default AccountProfile;